import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useApp, useSyncOnFocus } from "../context/AppContext";
import { platypusApi } from "../services/api";
import PageHeader from "../components/PageHeader";
import PlatypusLogo from "../components/PlatypusLogo";
import PlateoMascot from "../components/PlatypusMascot";
import CompanyLogo from "../components/CompanyLogo";
import EmptyState from "../components/EmptyState";
import TagInput from "../components/TagInput";
import ChipGroup from "../components/ChipGroup";
import AccessNotice, { UsageLeft } from "../components/AccessNotice";
import {
  WORK_TYPES,
  EMPLOYMENT_TYPES,
  EXPERIENCE_LEVELS,
  LEVEL_HINTS,
  COMPANY_TYPES,
  COMPANY_TYPE_HELP,
  VISA_OPTIONS,
  VISA_LABELS,
  VISA_HELP,
} from "../data/jobFilters";
import { boardLabel, questionStats, reviewQueue } from "../utils/jobs";
import {
  extensionStatus,
  openApplication,
  openMessage,
} from "../utils/extension";
import { formatDate } from "../utils/format";

const SETTING_FIELDS = [
  "roles",
  "locations",
  "workTypes",
  "employmentTypes",
  "experienceLevels",
  "visa",
  "companyTypes",
  "excludeTitleWords",
  "excludeCompanies",
  "extraBoards",
  "batchSize",
  "minMatch",
  "maxAgeDays",
  "schedule",
];
const ATS_LABELS = { greenhouse: "Greenhouse", lever: "Lever", ashby: "Ashby" };

const MY_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
const zoneName = (zone) =>
  zone ? zone.split("/").pop().replace(/_/g, " ") : "the server's";

const settingsOf = (platypus) => {
  const out = Object.fromEntries(SETTING_FIELDS.map((f) => [f, platypus[f]]));
  out.schedule = {
    enabled: platypus.schedule.enabled,
    time: platypus.schedule.time,
    timeZone: platypus.schedule.timeZone || MY_ZONE,
  };
  return out;
};

function ScheduleZone({ saved, enabled, onUseMine }) {
  if (!enabled) return null;
  if (!saved || saved === MY_ZONE) {
    return (
      <div className="form-text mt-1">
        In your time zone ({zoneName(MY_ZONE)}).
      </div>
    );
  }
  return (
    <div className="form-text mt-1">
      Runs on {zoneName(saved)} time, not yours ({zoneName(MY_ZONE)}).{" "}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 align-baseline"
        onClick={onUseMine}
      >
        Use my time zone
      </button>
    </div>
  );
}

function scoreColor(score) {
  if (score >= 80) return "#16a34a";
  if (score >= 65) return "#36454f";
  return "#d97706";
}

function MatchBadge({ score, size = 44 }) {
  return (
    <div
      className="match-badge"
      style={{ "--c": scoreColor(score), width: size, height: size }}
      title={`${score}% match`}
    >
      {score}
      <small>%</small>
    </div>
  );
}

function Readiness({ ready, extension }) {
  const items = [];
  if (!ready.ai || !ready.access) {
    items.push({
      bare: true,
      text: <AccessNotice feature="Platypus" className="" />,
    });
  }
  if (!ready.resume) {
    items.push({
      variant: "warning",
      text: (
        <>
          Upload your resume first. <Link to="/documents">Go to Documents</Link>
        </>
      ),
    });
  } else if (!ready.wordResume) {
    items.push({
      variant: "info",
      text: (
        <>
          Your default resume isn't a Word file, so applications will get your
          original resume instead of the tailored one.{" "}
          <Link to="/documents">Upload the .docx version</Link> to attach
          tailored PDFs in your exact format.
        </>
      ),
    });
  }
  if (!ready.template) {
    items.push({
      variant: "info",
      text: (
        <>
          No cover letter template yet, so cover letters will be skipped.{" "}
          <Link to="/documents#templates">Add a template</Link>
        </>
      ),
    });
  }
  if (extension?.stale) {
    items.push({
      variant: "warning",
      text: (
        <>
          The Chrome extension was just updated. Reload this page so{" "}
          <strong>Apply</strong> can fill in forms again.{" "}
          <button
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => window.location.reload()}
          >
            Reload now
          </button>
        </>
      ),
    });
  } else if (extension && !extension.connected) {
    items.push({
      variant: "info",
      text: (
        <>
          {extension.installed ? "Connect" : "Install"} the Chrome extension so{" "}
          <strong>Open application</strong> fills in the form for you.{" "}
          <Link to="/connect-extension">Set it up</Link>
        </>
      ),
    });
  }
  if (items.length === 0) return null;
  return (
    <div className="d-flex flex-column gap-2 mb-3">
      {items.map((item, i) =>
        item.bare ? (
          <div key={i}>{item.text}</div>
        ) : (
          <div
            key={i}
            className={`alert alert-${item.variant} py-2 small mb-0`}
          >
            {item.text}
          </div>
        ),
      )}
    </div>
  );
}

function RunCard({ data, form, onStart, onStop, onSchedule, starting }) {
  const { run } = data.platypus;
  const running = data.running || run.status === "running";
  const stats = run.stats || {};
  const canStart =
    data.ready.ai &&
    data.ready.access &&
    data.ready.resume &&
    form.roles.length > 0;
  const log = [...(run.log || [])].reverse().slice(0, 8);
  const pct = Math.round(((stats.prepared || 0) / form.batchSize) * 100);
  const finished =
    run.finishedAt &&
    ["done", "failed", "cancelled", "interrupted"].includes(run.status);
  const minutes = Math.max(2, Math.round(form.batchSize * 1.2));

  // One mounted mascot so start (face → run) and stop (run → face + wink) animate in place.
  return (
    <div className={`card mb-3 ${running ? "" : "platypus-start"}`.trim()}>
      <div className="card-body">
        <div className="d-flex flex-column flex-md-row gap-3 align-items-md-center">
          <div className={`platypus-mascot-stage${running ? " is-running" : ""}`}>
            <PlateoMascot running={running} size={120} />
          </div>
          <div className="flex-grow-1 min-w-0">
            {running ? (
              <>
                <div className="fw-semibold">Platypus is running</div>
                <div className="small text-muted text-truncate">
                  {run.step || "Working…"}
                </div>
              </>
            ) : (
              <>
                <h2 className="h6 mb-1">Prepare {form.batchSize} applications</h2>
                <p className="small text-muted mb-0">
                  Platypus keeps hunting in a loop: finds new jobs that fit you,
                  tailors your resume, writes cover letters and answers the
                  application questions. Takes about {minutes} minutes. Nothing is
                  submitted without you.
                </p>
              </>
            )}
          </div>
          <div className="d-grid flex-shrink-0">
            {running ? (
              <button
                className="btn btn-light"
                onClick={onStop}
                disabled={run.cancelRequested}
              >
                <i className="bi bi-stop-fill me-1" />
                {run.cancelRequested ? "Stopping…" : "Stop"}
              </button>
            ) : (
              <>
                <button
                  className="btn btn-primary btn-lg"
                  onClick={onStart}
                  disabled={!canStart || starting}
                >
                  {starting ? (
                    <span className="spinner-border spinner-border-sm me-2" />
                  ) : (
                    <i className="bi bi-play-fill me-1" />
                  )}
                  Start Platypus
                </button>
                <UsageLeft className="form-text text-center mt-1" />
              </>
            )}
          </div>
        </div>

        {running ? (
          <>
            <div className="run-stats mb-3 mt-3">
              <div>
                <div className="run-stat-value">{stats.searched || 0}</div>
                <div className="run-stat-label">Open postings</div>
              </div>
              <div>
                <div className="run-stat-value">{stats.candidates || 0}</div>
                <div className="run-stat-label">Fit your filters</div>
              </div>
              <div>
                <div className="run-stat-value">{stats.scored || 0}</div>
                <div className="run-stat-label">Checked by AI</div>
              </div>
              <div>
                <div className="run-stat-value">{stats.matched || 0}</div>
                <div className="run-stat-label">Good matches</div>
              </div>
              <div>
                <div className="run-stat-value text-success">
                  {stats.prepared || 0}
                  <span className="text-muted fw-normal fs-6">
                    /{form.batchSize}
                  </span>
                </div>
                <div className="run-stat-label">Ready</div>
              </div>
            </div>
            <div className="progress mb-3" style={{ height: 6 }}>
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${Math.max(pct, 4)}%` }}
              />
            </div>
            {log.length > 0 && (
              <ul className="run-log">
                {log.map((entry, i) => (
                  <li key={`${entry.at}-${i}`}>
                    <span className="text-muted">
                      {new Date(entry.at).toLocaleTimeString([], {
                        hour: "numeric",
                        minute: "2-digit",
                      })}
                    </span>
                    {entry.text}
                  </li>
                ))}
              </ul>
            )}
            <p className="small text-muted mb-0 mt-2">
              You can leave this page. Keep your Mac awake and Plateo running
              until it finishes.
            </p>
          </>
        ) : (
          <>
            {!form.roles.length && data.ready.resume && (
              <div className="small text-warning-emphasis mt-2">
                <i className="bi bi-arrow-down me-1" />
                Add the roles you're looking for below first.
              </div>
            )}
            <div className="schedule-row mt-3">
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="schedule-enabled"
                  checked={form.schedule.enabled}
                  onChange={(e) =>
                    onSchedule({ ...form.schedule, enabled: e.target.checked })
                  }
                />
                <label
                  className="form-check-label small"
                  htmlFor="schedule-enabled"
                >
                  Also run automatically every day at
                </label>
              </div>
              <input
                type="time"
                className="form-control form-control-sm"
                style={{ width: 120 }}
                value={form.schedule.time}
                onChange={(e) =>
                  e.target.value &&
                  onSchedule({ ...form.schedule, time: e.target.value })
                }
                aria-label="Daily run time"
              />
            </div>
            <ScheduleZone
              saved={data.platypus.schedule.timeZone}
              enabled={form.schedule.enabled}
              onUseMine={() => onSchedule({ ...form.schedule, timeZone: MY_ZONE })}
            />
            {finished && run.message && (
              <div
                className={`last-run ${run.status === "failed" ? "text-danger" : "text-muted"}`}
              >
                <i
                  className={`bi ${
                    run.status === "done"
                      ? "bi-check-circle text-success"
                      : run.status === "failed"
                        ? "bi-x-circle"
                        : "bi-pause-circle"
                  } me-1`}
                />
                Last run{" "}
                {formatDate(run.finishedAt, {
                  month: "short",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                })}
                {run.trigger === "schedule" ? " (scheduled)" : ""}: {run.message}
              </div>
            )}
            {finished && run.log?.length > 1 && (
              <details className="mt-2">
                <summary className="small text-muted">What happened</summary>
                <ul className="run-log mt-2">
                  {run.log.map((entry, i) => (
                    <li key={`${entry.at}-${i}`}>
                      <span className="text-muted">
                        {new Date(entry.at).toLocaleTimeString([], {
                          hour: "numeric",
                          minute: "2-digit",
                        })}
                      </span>
                      {entry.text}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function QueueRow({ job, onSkip }) {
  const { completeStep, toast } = useApp();
  const [opening, setOpening] = useState(false);
  const [opened, setOpened] = useState(false);
  const q = questionStats(job);

  const apply = async () => {
    setOpening(true);
    const result = await openApplication(job);
    setOpening(false);
    setOpened(true);
    const [message, variant] = openMessage(result);
    toast(message, variant);
  };

  const submitted = async () => {
    if (await completeStep(job.id, "applied", "Submitted application"))
      toast(`Marked as applied. ${job.company} moved to Applied`);
  };

  return (
    <div className="review-row">
      <MatchBadge score={job.automator.matchScore ?? 0} />
      <Link
        to={`/jobs/${job.id}`}
        className="flex-grow-1 min-w-0 text-body text-decoration-none"
      >
        <div className="d-flex align-items-center gap-2 min-w-0">
          <CompanyLogo name={job.company} size={20} />
          <span className="fw-medium text-truncate">{job.title}</span>
        </div>
        <div className="small text-muted text-truncate">
          {job.company}
          {job.location ? ` · ${job.location}` : ""}
        </div>
        {job.automator.matchReason && (
          <div className="small mt-1 review-reason">
            {job.automator.matchReason}
          </div>
        )}
        <div className="d-flex flex-wrap gap-1 mt-2">
          <span className="tag" title="Where this job is posted">
            {boardLabel(job)}
          </span>
          {job.steps?.resume && (
            <span className="tag tag-success">
              <i className="bi bi-check2" /> Resume tailored
            </span>
          )}
          {job.noCoverLetter ? (
            <span className="tag">No cover letter needed</span>
          ) : job.coverLetter ? (
            <span className="tag tag-success">
              <i className="bi bi-check2" /> Cover letter
            </span>
          ) : null}
          {q.total > 0 && (
            <span className="tag tag-success">{q.answered} answers</span>
          )}
          {q.needsYou > 0 && (
            <span className="tag tag-warning">
              <i className="bi bi-exclamation-circle" /> {q.needsYou} need
              {q.needsYou === 1 ? "s" : ""} you
            </span>
          )}
          {job.automator.concerns?.slice(0, 2).map((c) => (
            <span key={c} className="tag tag-warning">
              {c}
            </span>
          ))}
        </div>
      </Link>
      <div className="d-flex flex-column flex-sm-row gap-1 flex-shrink-0">
        {opened ? (
          <button
            className="btn btn-sm btn-success"
            onClick={submitted}
            title="Plateo marks it for you when it sees the confirmation page"
          >
            <i className="bi bi-check2-circle me-1" />
            Submitted
          </button>
        ) : (
          <button
            className="btn btn-sm btn-primary"
            onClick={apply}
            disabled={opening || !(job.applyUrl || job.url)}
            title="Open the application and fill it in"
          >
            {opening ? (
              <span className="spinner-border spinner-border-sm me-1" />
            ) : (
              <i className="bi bi-lightning-charge me-1" />
            )}
            Apply
          </button>
        )}
        <Link to={`/jobs/${job.id}`} className="btn btn-sm btn-light">
          Review
        </Link>
        <button
          className="btn btn-sm btn-light"
          onClick={() => onSkip(job)}
          title="Not interested"
        >
          Skip
        </button>
      </div>
    </div>
  );
}

function ReviewQueue({ jobs, onSkip }) {
  return (
    <div className="card mb-3">
      <div className="card-header-lite">
        <div>
          <h2 className="h6 mb-0">
            Ready for your review{" "}
            {jobs.length > 0 && (
              <span className="text-muted fw-normal">({jobs.length})</span>
            )}
          </h2>
          <div className="small text-muted">
            Apply opens the form and fills it in. Check it, submit, and Plateo
            marks the job applied when it sees the confirmation.
          </div>
        </div>
      </div>
      {jobs.length === 0 ? (
        <EmptyState
          icon="bi-inbox"
          title="Nothing to review yet"
          text="Prepared applications show up here, best matches first."
        />
      ) : (
        <div className="list-flush">
          {jobs.map((job) => (
            <QueueRow key={job.id} job={job} onSkip={onSkip} />
          ))}
        </div>
      )}
    </div>
  );
}

function CompaniesSection({ form, setField, defaultCompanies, toast }) {
  const [url, setUrl] = useState("");
  const [checking, setChecking] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const add = async (e) => {
    e.preventDefault();
    if (!url.trim()) return;
    setChecking(true);
    try {
      const { board, openJobs } = await platypusApi.checkBoard(url.trim());
      if (
        form.extraBoards.some(
          (b) => b.ats === board.ats && b.token === board.token,
        )
      ) {
        toast(`${board.company} is already on the list`, "info");
      } else {
        setField("extraBoards", [...form.extraBoards, board]);
        toast(
          `Added ${board.company} (${openJobs} open jobs). Save to keep it.`,
        );
      }
      setUrl("");
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setChecking(false);
    }
  };

  const shown = showAll ? defaultCompanies : defaultCompanies.slice(0, 8);
  return (
    <div>
      <label className="form-label small fw-medium">Companies to search</label>
      <p className="small text-muted mb-2">
        Searches the job boards of{" "}
        {defaultCompanies.length + form.extraBoards.length} companies (
        {shown.join(", ")}
        {!showAll && defaultCompanies.length > shown.length && (
          <>
            ,{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => setShowAll(true)}
            >
              {defaultCompanies.length - shown.length} more
            </button>
          </>
        )}
        ), plus{" "}
        <a
          href="https://github.com/SimplifyJobs/New-Grad-Positions"
          target="_blank"
          rel="noreferrer"
        >
          Simplify's
        </a>{" "}
        lists of open new-grad roles (when Entry level is picked) and
        internships (when Internship is picked), and Remotive for remote jobs.
      </p>
      {form.extraBoards.length > 0 && (
        <div className="d-flex flex-wrap gap-2 mb-2">
          {form.extraBoards.map((b) => (
            <span key={`${b.ats}:${b.token}`} className="tag tag-removable">
              {b.company}
              <span className="text-muted">· {ATS_LABELS[b.ats]}</span>
              <button
                type="button"
                aria-label={`Remove ${b.company}`}
                onClick={() =>
                  setField(
                    "extraBoards",
                    form.extraBoards.filter((x) => x !== b),
                  )
                }
              >
                <i className="bi bi-x" />
              </button>
            </span>
          ))}
        </div>
      )}
      <form className="input-group input-group-sm" onSubmit={add}>
        <input
          className="form-control"
          placeholder="Add a company: paste its job board link, e.g. jobs.lever.co/acme"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          aria-label="Company job board link"
        />
        <button
          className="btn btn-outline-primary"
          disabled={checking || !url.trim()}
        >
          {checking ? (
            <span className="spinner-border spinner-border-sm" />
          ) : (
            "Add"
          )}
        </button>
      </form>
      <div className="small text-muted mt-1">
        Works with companies that hire through Greenhouse, Lever or Ashby.
      </div>
    </div>
  );
}

function RoleTitlesHint({ roles, roleTitles }) {
  const [showAll, setShowAll] = useState(false);
  const current = roleTitles?.forRoles?.join("|") === roles.join("|");
  const others = current
    ? roleTitles.titles.filter(
        (t) => !roles.some((r) => r.toLowerCase() === t.toLowerCase()),
      )
    : [];
  if (others.length === 0) {
    return (
      <div className="small text-muted mt-1">
        Press Enter after each one. Similar titles are matched too.
      </div>
    );
  }
  const shown = showAll ? others : others.slice(0, 8);
  return (
    <div className="small text-muted mt-1">
      Also matches {shown.join(", ")}
      {others.length > shown.length && (
        <>
          ,{" "}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 align-baseline"
            onClick={() => setShowAll(true)}
          >
            {others.length - shown.length} more
          </button>
        </>
      )}
      .
    </div>
  );
}

function SettingsCard({
  form,
  setField,
  dirty,
  saving,
  onSave,
  onReset,
  defaultCompanies,
  roleTitles,
  toast,
}) {
  return (
    <div className="card">
      <div className="card-header-lite">
        <div>
          <h2 className="h6 mb-0">What to look for</h2>
          <div className="small text-muted">
            Used for every run, including the daily one.
          </div>
        </div>
      </div>
      <div className="card-body pt-0">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label small fw-medium" htmlFor="auto-roles">
              Roles
            </label>
            <TagInput
              id="auto-roles"
              value={form.roles}
              onChange={(v) => setField("roles", v)}
              placeholder="e.g. Software Engineer, Data Analyst"
              max={8}
            />
            <RoleTitlesHint roles={form.roles} roleTitles={roleTitles} />
          </div>
          <div className="col-md-6">
            <label
              className="form-label small fw-medium"
              htmlFor="auto-locations"
            >
              Locations
            </label>
            <TagInput
              id="auto-locations"
              value={form.locations}
              onChange={(v) => setField("locations", v)}
              placeholder="e.g. New York, Buffalo. Empty means anywhere"
              max={8}
            />
            <div className="small text-muted mt-1">
              Remote jobs are included when Remote is selected.
            </div>
          </div>
          <div className="col-12">
            <label className="form-label small fw-medium d-block">
              Experience level
            </label>
            <ChipGroup
              name="Experience level"
              options={EXPERIENCE_LEVELS}
              hints={LEVEL_HINTS}
              value={form.experienceLevels}
              onChange={(v) => setField("experienceLevels", v)}
            />
            <div className="small text-muted mt-1">
              Judged from the title and the years of experience the posting asks
              for. Postings that don't say are included and the AI judges them.
              None picked means any level.
            </div>
          </div>
          <div className="col-12">
            <label className="form-label small fw-medium d-block">Visa</label>
            <ChipGroup
              name="Visa"
              options={VISA_OPTIONS}
              labels={VISA_LABELS}
              value={form.visa}
              onChange={(v) => setField("visa", v)}
            />
            <div className="small text-muted mt-1">
              {VISA_HELP} None picked means any job.
            </div>
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-medium d-block">
              Work type
            </label>
            <ChipGroup
              name="Work type"
              options={WORK_TYPES}
              value={form.workTypes}
              onChange={(v) => setField("workTypes", v)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-medium d-block">
              Job type
            </label>
            <ChipGroup
              name="Job type"
              options={EMPLOYMENT_TYPES}
              value={form.employmentTypes}
              onChange={(v) => setField("employmentTypes", v)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label small fw-medium d-block">Company</label>
            <ChipGroup
              name="Company"
              options={COMPANY_TYPES}
              value={form.companyTypes || []}
              onChange={(v) => setField("companyTypes", v)}
            />
            <div className="small text-muted mt-1">{COMPANY_TYPE_HELP}</div>
          </div>
          <div className="col-sm-4">
            <label className="form-label small fw-medium" htmlFor="auto-batch">
              Jobs per run
            </label>
            <select
              id="auto-batch"
              className="form-select"
              value={form.batchSize}
              onChange={(e) => setField("batchSize", Number(e.target.value))}
            >
              {[3, 5, 10, 15, 20, 25].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-4">
            <label className="form-label small fw-medium" htmlFor="auto-match">
              How picky
            </label>
            <select
              id="auto-match"
              className="form-select"
              value={form.minMatch}
              onChange={(e) => setField("minMatch", Number(e.target.value))}
            >
              <option value={60}>Include stretch roles (60%+)</option>
              <option value={70}>Good fits (70%+)</option>
              <option value={80}>Strong fits only (80%+)</option>
            </select>
          </div>
          <div className="col-sm-4">
            <label className="form-label small fw-medium" htmlFor="auto-age">
              Posted within
            </label>
            <select
              id="auto-age"
              className="form-select"
              value={form.maxAgeDays}
              onChange={(e) => setField("maxAgeDays", Number(e.target.value))}
            >
              {[7, 14, 30, 60].map((n) => (
                <option key={n} value={n}>
                  {n} days
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-6">
            <label
              className="form-label small fw-medium"
              htmlFor="auto-exclude-titles"
            >
              Skip titles containing
            </label>
            <TagInput
              id="auto-exclude-titles"
              value={form.excludeTitleWords}
              onChange={(v) => setField("excludeTitleWords", v)}
              placeholder="e.g. Manager, Sales"
            />
          </div>
          <div className="col-md-6">
            <label
              className="form-label small fw-medium"
              htmlFor="auto-exclude-companies"
            >
              Skip companies
            </label>
            <TagInput
              id="auto-exclude-companies"
              value={form.excludeCompanies}
              onChange={(v) => setField("excludeCompanies", v)}
              placeholder="Companies you don't want"
              max={50}
            />
          </div>
          <div className="col-12">
            <CompaniesSection
              form={form}
              setField={setField}
              defaultCompanies={defaultCompanies}
              toast={toast}
            />
          </div>
        </div>
      </div>
      {dirty && (
        <div className="settings-footer">
          <span className="small text-muted me-auto">
            You have unsaved changes
          </span>
          <button
            className="btn btn-light btn-sm"
            onClick={onReset}
            disabled={saving}
          >
            Undo
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onSave}
            disabled={saving}
          >
            {saving && (
              <span className="spinner-border spinner-border-sm me-1" />
            )}
            Save
          </button>
        </div>
      )}
    </div>
  );
}

function Platypus() {
  const { jobs, toast, refreshJobs, refreshStatus, deleteJob } = useApp();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [extension, setExtension] = useState(null);
  const lastPrepared = useRef(0);
  useSyncOnFocus();

  const apply = useCallback((next, { keepForm = false } = {}) => {
    setData(next);
    if (!keepForm) setForm(settingsOf(next.platypus));
  }, []);

  useEffect(() => {
    platypusApi
      .get()
      .then((next) => apply(next))
      .catch((err) => setError(err.message));
    extensionStatus().then(setExtension);
  }, [apply]);

  const running =
    !!data && (data.running || data.platypus.run.status === "running");

  // Poll while a run is going; pull in prepared jobs as they arrive.
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(async () => {
      try {
        const next = await platypusApi.get();
        setData(next);
        const { run } = next.platypus;
        if ((run.stats?.prepared || 0) !== lastPrepared.current) {
          lastPrepared.current = run.stats?.prepared || 0;
          refreshJobs();
        }
        if (!next.running && run.status !== "running") {
          refreshJobs();
          refreshStatus();
          toast(
            run.message || "Platypus finished",
            run.status === "failed" ? "danger" : "success",
          );
        }
      } catch {
        // Try again on the next tick.
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [running, refreshJobs, refreshStatus, toast]);

  if (error) {
    return (
      <div className="card mt-4">
        <EmptyState
          icon="bi-exclamation-triangle"
          title="Couldn't load Platypus"
          text={error}
        />
      </div>
    );
  }
  if (!data || !form) {
    return (
      <div className="text-center text-muted py-5">
        <span className="spinner-border spinner-border-sm me-2" /> Loading…
      </div>
    );
  }

  const saved = settingsOf(data.platypus);
  const dirty = JSON.stringify(saved) !== JSON.stringify(form);
  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const save = async (settings = form) => {
    setSaving(true);
    try {
      apply(await platypusApi.update(settings));
      return true;
    } catch (err) {
      toast(err.message, "danger");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const start = async () => {
    setStarting(true);
    try {
      if (dirty && !(await save())) return;
      lastPrepared.current = 0;
      apply(await platypusApi.run());
      toast(
        "Platypus started. Prepared jobs will appear below as they're ready.",
      );
    } catch (err) {
      toast(err.message, "danger");
    } finally {
      setStarting(false);
    }
  };

  const stop = async () => {
    try {
      apply(await platypusApi.cancel(), { keepForm: true });
    } catch (err) {
      toast(err.message, "danger");
    }
  };

  const changeSchedule = async (schedule) => {
    setField("schedule", schedule);
    try {
      const next = await platypusApi.update({ schedule });
      setData(next);
      setForm((f) => ({ ...f, schedule: settingsOf(next.platypus).schedule }));
      toast(
        schedule.enabled
          ? `Runs every day at ${schedule.time}, ${zoneName(schedule.timeZone)} time`
          : "Daily run turned off",
        "info",
      );
    } catch (err) {
      toast(err.message, "danger");
    }
  };

  const skip = async (job) => {
    if (await deleteJob(job.id))
      toast(
        `Skipped ${job.title} at ${job.company}. It won't come back.`,
        "info",
      );
  };

  const queue = reviewQueue(jobs);

  return (
    <>
      <PageHeader
        icon={
          <span className="page-title-logo">
            <PlatypusLogo size={32} />
          </span>
        }
        title="Platypus"
        subtitle="Your AI agent for applying to jobs. Platypus hunts for jobs that fit you in a loop and prepares each application. You review, open it, and submit."
      />
      <Readiness ready={data.ready} extension={extension} />
      <RunCard
        data={data}
        form={form}
        onStart={start}
        onStop={stop}
        onSchedule={changeSchedule}
        starting={starting}
      />
      <ReviewQueue jobs={queue} onSkip={skip} />
      <SettingsCard
        form={form}
        setField={setField}
        dirty={dirty}
        saving={saving}
        onSave={() => save()}
        onReset={() => setForm(saved)}
        defaultCompanies={data.defaultCompanies}
        roleTitles={data.platypus.roleTitles}
        toast={toast}
      />
    </>
  );
}

export default Platypus;
